import type { UserDto } from '@/core/data/user/user.interface';
import { localize, localizeWithValues } from '@/core/gambits';

export const getPlayerAge = (player: any): number | undefined => {
  const { age } = player || {};

  if (!age) return;

  return age;
};

export const getHumanizedAge = (player?: UserDto | any): string | undefined => {
  const { age } = player || {};

  if (!age) return 'Unknown age';

  if (age < 20) return 'Teens';
  if (age < 30) return '20s';
  if (age < 40) return '30s';
  if (age < 50) return '40s';
  if (age < 60) return '50s';
  if (age < 70) return '60s';
  if (age < 80) return '70s';
  if (age < 90) return '80s';
  if (age < 100) return '90s';

  return '100+ years';
};

export enum UnitSystem {
  IMPERIAL = 'imperial',
  METRIC = 'metric',
}

export const humanizeMetricDistance = (distance: number, units: UnitSystem = UnitSystem.METRIC) => {
  if (distance < 100) {
    return 'Here';
  }

  if (distance < 1000) {
    return 'Short walk';
  }

  if (distance < 3000) {
    return 'Long walk';
  }

  if (distance < 15000) {
    return 'Short ride';
  }

  if (distance < 50000) {
    return 'Long ride';
  }

  const distanceInKm = distance / 1000;
  const distanceInMiles = distanceInKm * 0.6213711922;

  if (units === UnitSystem.IMPERIAL) {
    return localizeWithValues('locations.common.away', [`${Math.floor(distanceInMiles / 10)}0+ mi`]);
  } else {
    return localizeWithValues('locations.common.away', [`${Math.floor(distanceInKm / 10)}0+ km`]);
  }
};

export const getSimpleHumanizedDistance = (
  player?: UserDto | any,
  units: UnitSystem = UnitSystem.METRIC,
): string | undefined => {
  const { distance } = player || {};

  if (!distance && distance !== 0) return '';

  if (distance === 0) return localize('locations.common.here');

  const distanceInKm = distance / 1000;

  if (distanceInKm > 999) {
    return localize('locations.common.farAway');
  }

  if (units === UnitSystem.IMPERIAL) {
    const distanceInMiles = distanceInKm * 0.6213711922;
    const formattedDistance = distanceInMiles < 10 ? distanceInMiles.toFixed(1) : Math.floor(distanceInMiles);
    return localizeWithValues('locations.common.away', [`${formattedDistance} mi`]);
  }

  const formattedDistance =
    distance < 1000
      ? `${Math.floor(distance)} m`
      : distanceInKm < 10
        ? `${distanceInKm.toFixed(1)} km`
        : `${Math.floor(distanceInKm)} km`;

  return localizeWithValues('locations.common.away', [formattedDistance]);
};
